import { formatLocalDate } from "../lib/utils";
import { useState, useEffect, useRef } from "react";
import { useParams, Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { StatusBadge } from "../components/StatusBadge";
import { 
  Smartphone, 
  CheckCircle2, 
  Clock, 
  Star,
  ArrowLeft,
  Loader2,
  Image,
  FileText,
  Download,
  CreditCard,
  XCircle,
  RefreshCw,
  MessageCircle,
  Send,
  Receipt,
  Eye,
  PenTool,
  RotateCcw,
  AlertTriangle
} from "lucide-react";
import { toast, Toaster } from "sonner";
import axios from "axios";
import SignatureCanvas from "react-signature-canvas";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_ORDER = ["reception", "diagnostic", "diagnostic_advanced", "quote_pending", "quote_accepted", "pending_parts", "in_repair", "testing", "ready", "delivered"];

function PhotoGallery({ photos }) {
  const [activeTab, setActiveTab] = useState("all");
  const [shopName, setShopName] = useState("AMCPhone Martinique");
  const [lightbox, setLightbox] = useState(null); // { url, label, index }

  if (!photos || photos.length === 0) return null;

  const before = photos.filter(p => p.photo_type === "before");
  const after  = photos.filter(p => p.photo_type === "after");
  const tabs = [
    { id: "all",    label: "Toutes",  count: photos.length },
    { id: "before", label: "Avant",   count: before.length },
    { id: "after",  label: "Après",   count: after.length  },
  ].filter(t => t.count > 0);

  const displayed = activeTab === "all" ? photos : activeTab === "before" ? before : after;

  const openLightbox = (photo, idx) => {
    setLightbox({ url: API_URL + photo.image_url, label: photo.photo_type === "before" ? "Avant" : "Après", index: idx });
  };
  const closeLightbox = () => setLightbox(null);
  const navLightbox = (dir) => {
    const newIdx = (lightbox.index + dir + displayed.length) % displayed.length;
    openLightbox(displayed[newIdx], newIdx);
  };

  return (
    <>
      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <div className="relative max-w-2xl w-full" onClick={e => e.stopPropagation()}>
            {/* Close */}
            <button
              onClick={closeLightbox}
              className="absolute -top-10 right-0 text-white/70 hover:text-white text-sm flex items-center gap-1"
            >
              <XCircle className="w-5 h-5" /> Fermer
            </button>
            {/* Image */}
            <img
              src={lightbox.url}
              alt={lightbox.label}
              className="w-full max-h-[75vh] object-contain rounded-xl"
            />
            {/* Label */}
            <div className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-semibold bg-black/60 text-white">
              {lightbox.label}
            </div>
            {/* Nav arrows */}
            {displayed.length > 1 && (
              <>
                <button
                  onClick={() => navLightbox(-1)}
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 hover:bg-black/80 text-white flex items-center justify-center text-lg"
                >‹</button>
                <button
                  onClick={() => navLightbox(1)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 hover:bg-black/80 text-white flex items-center justify-center text-lg"
                >›</button>
              </>
            )}
            {/* Counter */}
            <div className="absolute bottom-3 right-3 px-2 py-1 rounded bg-black/60 text-white text-xs">
              {lightbox.index + 1} / {displayed.length}
            </div>
          </div>
        </div>
      )}

      {/* Gallery card */}
      <Card className="bg-card border-border">
        <CardContent className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Image className="w-5 h-5 text-primary" />
              <p className="font-medium text-foreground">Photos de votre appareil</p>
            </div>
            <span className="text-xs text-muted-foreground bg-secondary/50 px-2 py-1 rounded-full">
              {photos.length} photo{photos.length > 1 ? "s" : ""}
            </span>
          </div>

          {/* Tabs */}
          {tabs.length > 1 && (
            <div className="flex gap-2 mb-4">
              {tabs.map(t => (
                <button
                  key={t.id}
                  onClick={() => setActiveTab(t.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                    activeTab === t.id
                      ? "bg-primary text-white"
                      : "bg-secondary/50 text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {t.label} ({t.count})
                </button>
              ))}
            </div>
          )}

          {/* Grid */}
          <div className="grid grid-cols-2 gap-3">
            {displayed.map((photo, idx) => (
              <div
                key={photo.id}
                className="relative group cursor-pointer rounded-lg overflow-hidden"
                onClick={() => openLightbox(photo, idx)}
              >
                <img
                  src={API_URL + photo.image_url}
                  alt={photo.photo_type}
                  className="w-full h-36 object-cover transition-transform duration-200 group-hover:scale-105"
                />
                {/* Overlay on hover */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
                  <Eye className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                {/* Badge avant/après */}
                <div className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-xs font-semibold ${
                  photo.photo_type === "before"
                    ? "bg-amber-500/90 text-white"
                    : "bg-emerald-500/90 text-white"
                }`}>
                  {photo.photo_type === "before" ? "Avant" : "Après"}
                </div>
                {/* Description */}
                {photo.description && (
                  <div className="absolute bottom-0 left-0 right-0 px-2 py-1.5 bg-gradient-to-t from-black/70 to-transparent text-xs text-white truncate">
                    {photo.description}
                  </div>
                )}
              </div>
            ))}
          </div>

          <p className="text-xs text-muted-foreground text-center mt-3">
            Appuyez sur une photo pour l'agrandir
          </p>
        </CardContent>
      </Card>
    </>
  );
}

function InvoiceCard({ invoicePdf, invoiceAmount, invoicePaid, ticketNumber, deposits, totalDeposited, remainingBalance }) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [showPayOnSite, setShowPayOnSite] = useState(false);
  
  if (!invoicePdf && !invoiceAmount) {
    return null;
  }

  const handlePayment = async () => {
    setIsProcessing(true);
    try {
      const response = await axios.post(API_URL + "/api/track/" + ticketNumber + "/create-payment");
      
      if (response.data && response.data.payment_url) {
        toast.success("Redirection vers la page de paiement...");
        window.location.href = response.data.payment_url;
      } else {
        toast.error("Erreur lors de la création du paiement");
      }
    } catch (err) {
      const msg = err.response && err.response.data && err.response.data.detail;
      toast.error(msg || "Erreur lors de la création du paiement");
    } finally {
      setIsProcessing(false);
    }
  };
  
  return (
    <Card className="bg-card border-border border-emerald-500/30">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <FileText className="w-5 h-5 text-emerald-500" />
          <p className="font-medium text-foreground">Votre facture</p>
        </div>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Montant total</span>
            <span className="text-2xl font-bold text-emerald-500">{invoiceAmount ? invoiceAmount.toFixed(2) : "0.00"}€</span>
          </div>

          {/* Avances */}
          {totalDeposited > 0 && (
            <>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Avance(s) versée(s)</span>
                <span className="text-amber-500 font-medium">− {totalDeposited.toFixed(2)}€</span>
              </div>
              <div className="flex items-center justify-between border-t border-border pt-2">
                <span className="font-semibold">Reste à payer</span>
                <span className={`text-xl font-bold ${remainingBalance <= 0 ? "text-emerald-500" : "text-primary"}`}>
                  {remainingBalance <= 0 ? "✓ Soldé" : `${remainingBalance.toFixed(2)}€`}
                </span>
              </div>
            </>
          )}
          
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Statut</span>
            {invoicePaid ? (
              <span className="text-emerald-500 flex items-center gap-1">
                <CheckCircle2 className="w-4 h-4" /> Payée
              </span>
            ) : (
              <span className="text-amber-500">En attente de paiement</span>
            )}
          </div>
          
          <div className="flex gap-2 pt-3">
            {invoicePdf && (
              <Button 
                variant="outline"
                className="flex-1"
                onClick={function() { 
                  const pdfPath = invoicePdf.startsWith('/api') ? invoicePdf : '/api' + invoicePdf;
                  window.open(API_URL + pdfPath, "_blank"); 
                }}
              >
                <Download className="w-4 h-4 mr-2" />
                Télécharger
              </Button>
            )}
            {!invoicePaid && invoiceAmount > 0 && (remainingBalance === null || remainingBalance === undefined || remainingBalance > 0) && (
              <>
                <Button 
                  className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                  onClick={handlePayment}
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Chargement...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" />
                      {remainingBalance !== null && remainingBalance !== undefined && remainingBalance < invoiceAmount
                        ? `Payer le solde (${remainingBalance.toFixed(2)}€)`
                        : "Payer en ligne"}
                    </>
                  )}
                </Button>
              </>
            )}
          </div>
          
          {/* Option payer sur place */}
          {!invoicePaid && invoiceAmount > 0 && (remainingBalance === null || remainingBalance === undefined || remainingBalance > 0) && (
            <div className="pt-3 border-t border-border">
              <button 
                onClick={() => setShowPayOnSite(!showPayOnSite)}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
              >
                <span>{showPayOnSite ? "▼" : "▶"}</span>
                Payer sur place
              </button>
              
              {showPayOnSite && (
                <div className="mt-3 p-4 bg-secondary/30 rounded-lg">
                  <p className="text-sm text-foreground mb-3">
                    Vous pouvez également régler sur place lors de la récupération de votre appareil. Modes de paiement acceptés :
                  </p>
                  <div className="flex flex-wrap gap-3 justify-center">
                    {/* Espèces */}
                    <div className="flex flex-col items-center gap-1">
                      <div className="w-12 h-12 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-500">
                          <rect x="2" y="6" width="20" height="12" rx="2"/>
                          <circle cx="12" cy="12" r="2"/>
                          <path d="M6 12h.01M18 12h.01"/>
                        </svg>
                      </div>
                      <span className="text-xs text-muted-foreground">Espèces</span>
                    </div>
                    
                    {/* Carte bancaire */}
                    <div className="flex flex-col items-center gap-1">
                      <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-500">
                          <rect x="2" y="5" width="20" height="14" rx="2"/>
                          <line x1="2" y1="10" x2="22" y2="10"/>
                        </svg>
                      </div>
                      <span className="text-xs text-muted-foreground">Carte</span>
                    </div>
                    
                    {/* Wero */}
                    <div className="flex flex-col items-center gap-1">
                      <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
                        <span className="text-purple-500 font-bold text-sm">W</span>
                      </div>
                      <span className="text-xs text-muted-foreground">Wero</span>
                    </div>
                    
                    {/* Virement */}
                    <div className="flex flex-col items-center gap-1">
                      <div className="w-12 h-12 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-cyan-500">
                          <path d="M17 3v18"/>
                          <path d="M7 3v18"/>
                          <path d="M12 7l5-4"/>
                          <path d="M12 7L7 3"/>
                          <path d="M12 17l5 4"/>
                          <path d="M12 17l-5 4"/>
                        </svg>
                      </div>
                      <span className="text-xs text-muted-foreground">Virement</span>
                    </div>
                    
                    {/* PayPal */}
                    <div className="flex flex-col items-center gap-1">
                      <div className="w-12 h-12 bg-sky-500/20 rounded-lg flex items-center justify-center">
                        <span className="text-sky-500 font-bold text-sm">PP</span>
                      </div>
                      <span className="text-xs text-muted-foreground">PayPal</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Documents Section (Quotes & Invoices)
function DocumentsSection({ ticketNumber }) {
  const [documents, setDocuments] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDocuments = () => {
    axios.get(API_URL + "/api/track/" + ticketNumber + "/documents")
      .then(res => setDocuments(res.data))
      .catch(err => console.error("Error:", err))
      .finally(() => setLoading(false));
  };

  
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/shop-info`)
      .then(r => r.json()).then(d => { if (d.shop_name) { setShopName(d.shop_name); document.title = `Suivi réparation — ${d.shop_name}`; } }).catch(() => {});
  }, []);

useEffect(() => {
    fetchDocuments();
  }, [ticketNumber]);

  if (loading || !documents) return null;
  
  const hasQuotes    = documents.quotes && documents.quotes.length > 0;
  const hasInvoices  = documents.invoices && documents.invoices.length > 0;
  const extraDocs    = documents.extra_docs || [];
  const partsNeedingOrder = documents.parts_needing_order || [];
  if (!hasQuotes && !hasInvoices && extraDocs.length === 0) return null;
  return (
    <Card className="bg-card border-border">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <Receipt className="w-5 h-5 text-amber-500" />
          <p className="font-medium text-foreground">Documents disponibles</p>
        </div>
        {extraDocs.length > 0 && (
          <div className="mb-4 space-y-2">
            {extraDocs.map(doc => (
              <div key={doc.key} className="flex items-center justify-between p-3 rounded-xl bg-secondary/30 border border-border">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{doc.icon}</span>
                  <div>
                    <p className="text-sm font-medium text-foreground">{doc.label}</p>
                    {doc.date && <p className="text-xs text-muted-foreground">{new Date(doc.date).toLocaleDateString("fr-FR")}</p>}
                    {doc.amount && <p className="text-xs font-semibold text-foreground">{doc.amount.toFixed(2)}€ {doc.paid ? "✅ Payé" : "⏳ En attente"}</p>}
                  </div>
                </div>
                <div className="flex gap-1">
                  <a href={`${process.env.REACT_APP_BACKEND_URL}${doc.url.startsWith("/api") ? doc.url : "/api" + doc.url}`} target="_blank" rel="noreferrer"
                    className="text-xs px-3 py-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 font-medium">Voir</a>
                  <a href={`${process.env.REACT_APP_BACKEND_URL}${doc.url.startsWith("/api") ? doc.url : "/api" + doc.url}`} download
                    className="text-xs px-3 py-1.5 rounded-lg bg-secondary text-muted-foreground hover:bg-secondary/80 font-medium">↓</a>
                </div>
              </div>
            ))}
          </div>
        )}
        {hasQuotes && (
          <div className="mb-4">
            <p className="text-sm text-muted-foreground mb-2">Devis</p>
            <QuotesList quotes={documents.quotes} ticketNumber={ticketNumber} onUpdate={fetchDocuments} partsNeedingOrder={partsNeedingOrder} />
          </div>
        )}
        {hasInvoices && (
          <div>
            <p className="text-sm text-muted-foreground mb-2">Factures</p>
            <InvoicesList invoices={documents.invoices} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function QuotesList({ quotes, ticketNumber, onUpdate, partsNeedingOrder }) {
  return (
    <div className="space-y-2">
      {quotes.map(quote => (
        <QuoteItem key={quote.id} quote={quote} ticketNumber={ticketNumber} onUpdate={onUpdate} partsNeedingOrder={partsNeedingOrder} />
      ))}
    </div>
  );
}

function QuoteItem({ quote, ticketNumber, onUpdate, partsNeedingOrder = [] }) {
  const [processing, setProcessing] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showSignatureDialog, setShowSignatureDialog] = useState(false);
  const [showDelayAlert, setShowDelayAlert] = useState(false);
  const [partsToOrder, setPartsToOrder] = useState([]);
  const [rejectComment, setRejectComment] = useState("");
  const sigCanvasRef = useRef(null);
  
  // Check if there are parts needing order for this quote
  const hasPartsNeedingOrder = partsNeedingOrder && partsNeedingOrder.length > 0;

  const handleAcceptWithSignature = async () => {
    if (!sigCanvasRef.current || sigCanvasRef.current.isEmpty()) {
      toast.error("Veuillez signer avant d'accepter le devis");
      return;
    }
    
    setProcessing(true);
    try {
      const signatureData = sigCanvasRef.current.toDataURL("image/png");
      const response = await axios.post(API_URL + "/api/track/" + ticketNumber + "/quotes/" + quote.id + "/action", {
        action: "accept",
        signature: signatureData
      });
      
      setShowSignatureDialog(false);
      
      // Check if parts need to be ordered - show prominent dialog
      if (response.data.parts_to_order && response.data.parts_to_order.length > 0) {
        setPartsToOrder(response.data.parts_to_order);
        setShowDelayAlert(true);
      } else {
        toast.success("Devis accepté ! Nous allons procéder à la réparation.");
        onUpdate();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setProcessing(false);
    }
  };
  
  const handleCloseDelayAlert = () => {
    setShowDelayAlert(false);
    setPartsToOrder([]);
    onUpdate();
  };

  const handleReject = async () => {
    setProcessing(true);
    try {
      await axios.post(API_URL + "/api/track/" + ticketNumber + "/quotes/" + quote.id + "/action", {
        action: "reject",
        comment: rejectComment
      });
      toast.success("Devis refusé. Merci de nous avoir informé.");
      setShowRejectDialog(false);
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setProcessing(false);
    }
  };

  const clearSignature = () => {
    if (sigCanvasRef.current) {
      sigCanvasRef.current.clear();
    }
  };

  const downloadQuotePdf = async () => {
    try {
      const res = await axios.get(API_URL + "/api/track/" + ticketNumber + "/quotes/" + quote.id + "/pdf");
      if (res.data?.pdf_path) {
        window.open(API_URL + res.data.pdf_path, "_blank");
      }
    } catch (err) {
      toast.error("PDF non disponible");
    }
  };

  const hasResponded = quote.client_response;
  const hasPdf = quote.pdf_path || quote.signed_pdf_path;

  const viewPdf = () => {
    const pdfUrl = quote.signed_pdf_path || quote.pdf_path;
    if (pdfUrl) {
      window.open(API_URL + pdfUrl, "_blank");
    }
  };

  return (
    <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="font-medium">{quote.invoice_number}</p>
          <p className="text-xs text-muted-foreground">
            {new Date(quote.created_at).toLocaleDateString("fr-FR")}
          </p>
        </div>
        <span className="text-2xl font-bold text-amber-500">{quote.total.toFixed(2)}€</span>
      </div>

      {/* PDF View/Download - Always visible if PDF exists */}
      {hasPdf && (
        <div className="flex gap-2 mb-3">
          <Button 
            onClick={viewPdf}
            variant="outline"
            size="sm"
            className="flex-1"
          >
            <Eye className="w-4 h-4 mr-2" />
            Voir le devis
          </Button>
          <Button 
            onClick={downloadQuotePdf}
            variant="outline"
            size="sm"
            className="flex-1"
          >
            <Download className="w-4 h-4 mr-2" />
            Télécharger
          </Button>
        </div>
      )}
      
      {hasResponded ? (
        <div className="space-y-2">
          <div className={`p-2 rounded text-center text-sm ${
            quote.client_response === "accept" 
              ? "bg-emerald-500/20 text-emerald-500" 
              : "bg-red-500/20 text-red-500"
          }`}>
            {quote.client_response === "accept" ? "✅ Devis accepté" : "❌ Devis refusé"}
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          <Button 
            onClick={() => setShowSignatureDialog(true)}
            disabled={processing}
            className="flex-1 bg-emerald-500 hover:bg-emerald-600"
          >
            <PenTool className="w-4 h-4 mr-2" />
            Accepter et signer
          </Button>
          <Button 
            onClick={() => setShowRejectDialog(true)}
            disabled={processing}
            variant="outline"
            className="flex-1 border-red-500 text-red-500 hover:bg-red-500/10"
          >
            ✗ Refuser
          </Button>
        </div>
      )}
      
      {/* Signature Dialog */}
      {showSignatureDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <h3 className="font-medium text-lg mb-2 flex items-center gap-2">
              <PenTool className="w-5 h-5 text-emerald-500" />
              Signature pour accepter le devis
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              En signant, vous acceptez le devis de <strong>{quote.total.toFixed(2)}€</strong> et autorisez la réparation.
            </p>
            
            {/* Warning for parts needing order - BEFORE signature */}
            {hasPartsNeedingOrder && (
              <div className="bg-amber-500/10 border border-amber-500/40 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-500 mb-2">
                      Attention - Pièces en rupture de stock
                    </p>
                    <p className="text-sm text-foreground mb-3">
                      Sur le devis que vous vous apprêtez à signer, il y a des pièces en rupture de stock qui nécessitent une commande. Le délai de votre réparation peut être rallongé.
                    </p>
                    <div className="bg-card/50 rounded p-2 mb-2">
                      <p className="text-xs text-muted-foreground mb-1">Pièces concernées :</p>
                      <ul className="space-y-1">
                        {partsNeedingOrder.map((part, idx) => (
                          <li key={idx} className="text-sm flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                            <span>{part.part_name}</span>
                            <span className="text-muted-foreground">(×{part.quantity})</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="flex items-center gap-2 text-amber-500 text-sm">
                      <Clock className="w-4 h-4" />
                      <span>Délai supplémentaire estimé : +7 jours ouvrés</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div className="border-2 border-dashed border-border rounded-lg p-2 mb-4 bg-white">
              <SignatureCanvas
                ref={sigCanvasRef}
                canvasProps={{
                  className: "w-full h-40 cursor-crosshair",
                  style: { width: "100%", height: "160px" }
                }}
                backgroundColor="white"
              />
            </div>
            
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={clearSignature}
              className="mb-4 text-muted-foreground"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Effacer la signature
            </Button>
            
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => setShowSignatureDialog(false)}
                className="flex-1"
              >
                Annuler
              </Button>
              <Button 
                onClick={handleAcceptWithSignature}
                disabled={processing}
                className="flex-1 bg-emerald-500 hover:bg-emerald-600"
              >
                {processing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                )}
                {hasPartsNeedingOrder ? "Accepter malgré le délai" : "Confirmer l'acceptation"}
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Reject Dialog */}
      {showRejectDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg p-6 max-w-md w-full">
            <h3 className="font-medium text-lg mb-4">Refuser le devis</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Vous pouvez nous laisser un commentaire (optionnel) :
            </p>
            <Textarea
              value={rejectComment}
              onChange={(e) => setRejectComment(e.target.value)}
              placeholder="Raison du refus..."
              className="bg-secondary/50 mb-4"
            />
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => setShowRejectDialog(false)}
                className="flex-1"
              >
                Annuler
              </Button>
              <Button 
                onClick={handleReject}
                disabled={processing}
                className="flex-1 bg-red-500 hover:bg-red-600"
              >
                Confirmer le refus
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Parts Delay Alert Dialog */}
      {showDelayAlert && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-amber-500/50 rounded-lg p-6 max-w-md w-full shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-500" />
              </div>
              <div>
                <h3 className="font-bold text-lg text-foreground">Devis accepté !</h3>
                <p className="text-sm text-amber-500">Information importante</p>
              </div>
            </div>
            
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-4">
              <p className="text-foreground font-medium mb-2">
                ⚠️ Certaines pièces doivent être commandées
              </p>
              <p className="text-sm text-muted-foreground mb-3">
                Pour compléter votre réparation, les pièces suivantes doivent être commandées auprès de notre fournisseur :
              </p>
              <ul className="space-y-1 mb-3">
                {partsToOrder.map((part, idx) => (
                  <li key={idx} className="text-sm flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                    <span className="text-foreground">{part.name}</span>
                    <span className="text-muted-foreground">(×{part.quantity})</span>
                  </li>
                ))}
              </ul>
              <div className="flex items-center gap-2 text-amber-500 font-medium">
                <Clock className="w-4 h-4" />
                <span>Délai estimé : +7 jours ouvrés</span>
              </div>
            </div>
            
            <p className="text-sm text-muted-foreground mb-4">
              Nous vous informerons dès réception des pièces et nous procéderons immédiatement à la réparation.
            </p>
            
            <Button 
              onClick={handleCloseDelayAlert}
              className="w-full bg-primary hover:bg-primary/90"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Compris
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}


function InvoicesList({ invoices }) {
  return (
    <div className="space-y-2">
      {invoices.map(invoice => (
        <InvoiceItem key={invoice.id} invoice={invoice} />
      ))}
    </div>
  );
}

function InvoiceItem({ invoice }) {
  return (
    <div className="flex items-center justify-between p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
      <div>
        <p className="font-medium text-sm">{invoice.invoice_number}</p>
        <p className="text-xs text-muted-foreground">
          {new Date(invoice.created_at).toLocaleDateString("fr-FR")}
        </p>
      </div>
      <div className="flex items-center gap-3">
        <span className="font-bold text-emerald-500">{invoice.total.toFixed(2)}€</span>
        {invoice.is_paid ? (
          <span className="text-xs bg-emerald-500/20 text-emerald-500 px-2 py-1 rounded">Payée</span>
        ) : (
          <span className="text-xs bg-amber-500/20 text-amber-500 px-2 py-1 rounded">En attente</span>
        )}
      </div>
    </div>
  );
}

// Messages Section
function MessagesSection({ ticketNumber }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchMessages();
  }, [ticketNumber]);

  const fetchMessages = () => {
    axios.get(API_URL + "/api/track/" + ticketNumber + "/messages")
      .then(res => setMessages(res.data))
      .catch(err => console.error("Error:", err));
  };

  const handleSendMessage = () => {
    if (!newMessage.trim()) {
      toast.error("Veuillez écrire un message");
      return;
    }

    setSending(true);
    axios.post(API_URL + "/api/track/" + ticketNumber + "/messages", { message: newMessage.trim() })
      .then(() => {
        toast.success("Message envoyé !");
        setNewMessage("");
        fetchMessages();
      })
      .catch(err => toast.error(err.response?.data?.detail || "Erreur"))
      .finally(() => setSending(false));
  };

  return (
    <Card className="bg-card border-border">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <MessageCircle className="w-5 h-5 text-blue-500" />
          <p className="font-medium text-foreground">Envoyer un message au réparateur</p>
        </div>
        
        {messages.length > 0 && (
          <div className="mb-4 max-h-60 overflow-y-auto space-y-3">
            {messages.map(msg => (
              <MessageItem key={msg.id} msg={msg} />
            ))}
          </div>
        )}
        
        <div className="flex gap-2">
          <Textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Écrivez votre message ici..."
            className="bg-secondary/50 min-h-[80px]"
            disabled={sending}
          />
        </div>
        <Button 
          onClick={handleSendMessage}
          disabled={sending || !newMessage.trim()}
          className="mt-2 w-full bg-blue-500 hover:bg-blue-600"
        >
          {sending ? (
            <span className="flex items-center"><Loader2 className="w-4 h-4 mr-2 animate-spin" />Envoi...</span>
          ) : (
            <span className="flex items-center"><Send className="w-4 h-4 mr-2" />Envoyer le message</span>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}

function MessageItem({ msg }) {
  const isClient = msg.sender === "client";
  const dateStr = new Date(msg.created_at).toLocaleString("fr-FR", {
    day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit"
  });
  
  return (
    <div className={`p-3 rounded-lg ${isClient ? "bg-blue-500/10 border border-blue-500/20 ml-8" : "bg-secondary/50 border border-border mr-8"}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium">{isClient ? "Vous" : (msg.sender_name || "Réparateur")}</span>
        <span className="text-xs text-muted-foreground">{dateStr}</span>
      </div>
      <p className="text-sm">{msg.message}</p>
    </div>
  );
}

// Pickup Appointment Section
function PickupAppointmentSection({ ticketNumber, repair }) {
  const [appointment, setAppointment] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [preferredDate, setPreferredDate] = useState("");
  const [preferredTimeSlot, setPreferredTimeSlot] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Fetch existing appointment
    axios.get(API_URL + "/api/track/" + ticketNumber + "/pickup-appointment")
      .then(res => setAppointment(res.data))
      .catch(() => {});
  }, [ticketNumber]);

  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  const handleSubmit = () => {
    if (!preferredDate || !preferredTimeSlot) {
      toast.error("Veuillez sélectionner une date et un créneau");
      return;
    }

    setSubmitting(true);
    axios.post(API_URL + "/api/track/" + ticketNumber + "/pickup-appointment", {
      preferred_date: preferredDate,
      preferred_time_slot: preferredTimeSlot,
      notes: notes
    })
      .then(res => {
        toast.success("Rendez-vous confirmé !");
        setAppointment({
          preferred_date: preferredDate,
          preferred_time_slot: preferredTimeSlot
        });
        setShowForm(false);
      })
      .catch(err => toast.error(err.response?.data?.detail || "Erreur"))
      .finally(() => setSubmitting(false));
  };

  if (repair.status !== "ready") return null;

  const TIME_SLOTS = [
    { value: "09:00-12:00", label: "9h - 12h" },
    { value: "12:00-15:00", label: "12h - 15h" },
    { value: "15:00-18:00", label: "15h - 18h" }
  ];

  if (appointment) {
    return (
      <Card className="bg-emerald-500/10 border-emerald-500/30">
        <CardContent className="p-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
              <Clock className="w-5 h-5 text-emerald-500" />
            </div>
            <div>
              <p className="font-medium text-emerald-500 mb-1">Rendez-vous confirmé</p>
              <p className="text-sm text-foreground">
                📅 {formatLocalDate(appointment.preferred_date, { weekday: "long", day: "numeric", month: "long" })}
              </p>
              <p className="text-sm text-foreground">
                🕐 {appointment.preferred_time_slot}
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                N'oubliez pas d'apporter une pièce d'identité
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-card border-border">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="w-5 h-5 text-emerald-500" />
          <p className="font-medium text-foreground">Prendre RDV pour récupérer votre appareil</p>
        </div>

        {!showForm ? (
          <Button 
            onClick={() => setShowForm(true)}
            className="w-full bg-emerald-500 hover:bg-emerald-600"
          >
            Choisir une date de récupération
          </Button>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-muted-foreground block mb-1">Date</label>
                <Input
                  type="date"
                  value={preferredDate}
                  onChange={(e) => setPreferredDate(e.target.value)}
                  min={getMinDate()}
                  className="bg-secondary/50"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground block mb-1">Créneau</label>
                <select 
                  value={preferredTimeSlot}
                  onChange={(e) => setPreferredTimeSlot(e.target.value)}
                  className="w-full h-10 px-3 rounded-md bg-secondary/50 border border-border text-foreground"
                >
                  <option value="">Choisir...</option>
                  {TIME_SLOTS.map(slot => (
                    <option key={slot.value} value={slot.value}>{slot.label}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notes (optionnel)..."
              className="bg-secondary/50"
              rows={2}
            />

            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => setShowForm(false)}
                className="flex-1"
              >
                Annuler
              </Button>
              <Button 
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 bg-emerald-500 hover:bg-emerald-600"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmer le RDV"}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function StatusTimeline({ history, currentStatus }) {
  const currentIdx = STATUS_ORDER.indexOf(currentStatus);
  
  return (
    <div className="relative">
      {STATUS_ORDER.map(function(status, idx) {
        const historyEntry = history.find(function(h) { return h.status === status; });
        const isCompleted = idx <= currentIdx;
        const isCurrent = status === currentStatus;
        
        return (
          <div key={status} className="flex items-start gap-4 pb-8 last:pb-0">
            {/* Line */}
            <div className="flex flex-col items-center">
              <div className={
                "w-4 h-4 rounded-full border-2 flex items-center justify-center " +
                (isCompleted ? "bg-primary border-primary" : "bg-transparent border-muted-foreground/30")
              }>
                {isCompleted && <CheckCircle2 className="w-3 h-3 text-primary-foreground" />}
              </div>
              {idx < STATUS_ORDER.length - 1 && (
                <div className={
                  "w-0.5 h-full min-h-[40px] " +
                  (idx < currentIdx ? "bg-primary" : "bg-muted-foreground/20")
                } />
              )}
            </div>
            
            {/* Content */}
            <div className="flex-1 -mt-1">
              <div className="flex items-center gap-2">
                <StatusBadge status={status} />
                {isCurrent && (
                  <span className="px-2 py-0.5 bg-primary/20 text-primary text-xs rounded">
                    Actuel
                  </span>
                )}
              </div>
              {historyEntry && (
                <p className="text-xs text-muted-foreground mt-1">
                  {new Date(historyEntry.timestamp).toLocaleDateString('fr-FR', {
                    day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
                  })}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ReviewForm({ ticketNumber, onReviewSubmitted }) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await axios.post(API_URL + "/api/track/" + ticketNumber + "/review", {
        rating: rating,
        comment: comment || null
      });
      toast.success("Merci pour votre avis !");
      onReviewSubmitted();
    } catch (err) {
      const msg = err.response && err.response.data && err.response.data.detail;
      toast.error(msg || "Erreur lors de l'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="bg-card border-border">
      <CardContent className="p-6">
        <h3 className="font-chivo text-lg font-medium text-foreground mb-4">
          Donnez votre avis
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          Votre réparation est terminée ! Partagez votre expérience.
        </p>
        
        <div className="mb-4">
          <p className="text-sm text-muted-foreground mb-2">Note</p>
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map(function(star) {
              return (
                <button
                  key={star}
                  onClick={function() { setRating(star); }}
                  className="p-1 transition-transform hover:scale-110"
                >
                  <Star 
                    className={
                      "w-8 h-8 transition-colors duration-150 " +
                      (star <= rating ? "text-amber-500 fill-amber-500" : "text-muted-foreground")
                    }
                  />
                </button>
              );
            })}
          </div>
        </div>
        
        <div className="mb-4">
          <p className="text-sm text-muted-foreground mb-2">Commentaire (optionnel)</p>
          <Textarea
            value={comment}
            onChange={function(e) { setComment(e.target.value); }}
            placeholder="Partagez votre expérience..."
            className="bg-secondary/50"
            rows={3}
          />
        </div>
        
        <Button 
          onClick={handleSubmit} 
          disabled={submitting}
          className="w-full bg-primary hover:bg-primary/90"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Envoi...
            </>
          ) : (
            "Envoyer mon avis"
          )}
        </Button>
      </CardContent>
    </Card>
  );
}

function ExistingReview({ review }) {
  return (
    <Card className="bg-card border-border">
      <CardContent className="p-6">
        <h3 className="font-chivo text-lg font-medium text-foreground mb-4">
          Votre avis
        </h3>
        <div className="flex gap-1 mb-3">
          {[1, 2, 3, 4, 5].map(function(star) {
            return (
              <Star 
                key={star}
                className={
                  "w-6 h-6 " +
                  (star <= review.rating ? "text-amber-500 fill-amber-500" : "text-muted-foreground")
                }
              />
            );
          })}
        </div>
        {review.comment && (
          <p className="text-muted-foreground">{review.comment}</p>
        )}
        <p className="text-xs text-muted-foreground mt-3">Merci pour votre retour !</p>
      </CardContent>
    </Card>
  );
}

export default function TrackingPage() {
  const { ticketNumber } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [repair, setRepair] = useState(null);
  const [reviewInfo, setReviewInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCancelledDialog, setShowCancelledDialog] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [shopName, setShopName] = useState("AMCPhone Martinique");
  useEffect(() => { fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/shop-info`).then(r=>r.json()).then(d=>{ if(d.shop_name) setShopName(d.shop_name); }).catch(()=>{}); }, []);

  useEffect(function() {
    fetchData();
    
    // Check payment status from URL params
    const paymentStatus = searchParams.get("payment");
    if (paymentStatus === "success") {
      toast.success("Paiement effectué avec succès ! Merci.", { duration: 6000 });
      // Clean URL
      setSearchParams({});
    } else if (paymentStatus === "cancelled") {
      // Show cancellation dialog
      setShowCancelledDialog(true);
      // Clean URL
      setSearchParams({});
    }
  }, [ticketNumber]);

  const handleRetryPayment = async () => {
    setIsRetrying(true);
    try {
      const response = await axios.post(API_URL + "/api/track/" + ticketNumber + "/create-payment");
      if (response.data && response.data.payment_url) {
        window.location.href = response.data.payment_url;
      } else {
        toast.error("Erreur lors de la création du paiement");
        setShowCancelledDialog(false);
      }
    } catch (err) {
      const msg = err.response && err.response.data && err.response.data.detail;
      toast.error(msg || "Erreur lors de la création du paiement");
      setShowCancelledDialog(false);
    } finally {
      setIsRetrying(false);
    }
  };

  const handleCloseDialog = () => {
    setShowCancelledDialog(false);
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const repairRes = await axios.get(API_URL + "/api/track/" + ticketNumber);
      setRepair(repairRes.data);
      
      const reviewRes = await axios.get(API_URL + "/api/track/" + ticketNumber + "/review");
      setReviewInfo(reviewRes.data);
    } catch (err) {
      const msg = err.response && err.response.data && err.response.data.detail;
      setError(msg || "Réparation non trouvée");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white text-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white text-gray-900 flex items-center justify-center p-4">
        <Card className="bg-card border-border max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-destructive/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Smartphone className="w-8 h-8 text-destructive" />
            </div>
            <h1 className="font-chivo text-xl font-bold text-foreground mb-2">
              Réparation non trouvée
            </h1>
            <p className="text-muted-foreground mb-6">{error}</p>
            <p className="text-sm text-muted-foreground">
              Vérifiez le numéro de ticket: <code className="bg-secondary px-2 py-1 rounded">{ticketNumber}</code>
            </p>
          </CardContent>
        </Card>
        <Toaster position="top-right" richColors />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* Header */}
      <header className="bg-card border-b border-border">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="/logo.png" 
                alt="AMCPhone" 
                className="w-10 h-10 rounded-md object-contain"
              />
              <div>
                <h1 className="font-chivo font-bold text-lg text-foreground">{shopName}</h1>
                <p className="text-xs text-muted-foreground">Suivi de réparation</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Ticket Info */}
            <Card className="bg-card border-border">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <p className="text-sm text-muted-foreground">Numéro de ticket</p>
                    <h2 className="font-mono text-2xl font-bold text-primary">{repair.ticket_number}</h2>
                  </div>
                  <StatusBadge status={repair.status} />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Appareil</p>
                    <p className="font-medium">{repair.device_brand} {repair.device_model}</p>
                    <p className="text-xs text-muted-foreground capitalize">{repair.device_type}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Date de dépôt</p>
                    <p className="font-medium">
                      {new Date(repair.created_at).toLocaleDateString('fr-FR', {
                        day: 'numeric', month: 'long', year: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
                
                <div className="mt-4 pt-4 border-t border-border">
                  <p className="text-sm text-muted-foreground mb-2">Problème signalé</p>
                  <p className="bg-secondary/30 p-3 rounded-md text-sm">{repair.problem_description}</p>
                </div>

                {repair.diagnosis && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <p className="text-sm text-muted-foreground mb-2 flex items-center gap-2">
                      <span className="text-blue-500">🔍</span> Compte rendu du diagnostic
                    </p>
                    <p className="bg-blue-500/10 border border-blue-500/20 p-3 rounded-md text-sm whitespace-pre-line">{repair.diagnosis}</p>
                  </div>
                )}

                {repair.estimated_cost && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <div>
                      <p className="text-sm text-muted-foreground">Estimation</p>
                      <p className="font-medium">{repair.estimated_cost.toFixed(2)}€</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Progress Bar */}
            <Card className="bg-card border-border">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-medium text-foreground">Progression</p>
                  <p className="text-sm text-primary font-bold">{repair.progress_percent || 0}%</p>
                </div>
                <div className="w-full h-3 bg-secondary rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-primary to-emerald-400 rounded-full transition-all duration-500"
                    style={{ width: (repair.progress_percent || 0) + "%" }}
                  />
                </div>
                {repair.estimated_duration_hours && (
                  <p className="text-xs text-muted-foreground mt-2">
                    <Clock className="w-3 h-3 inline mr-1" />
                    Durée estimée: {repair.estimated_duration_hours}h
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Photos */}
            <PhotoGallery photos={repair.photos} />

            {/* Invoice */}
            <InvoiceCard 
              invoicePdf={repair.invoice_pdf} 
              invoiceAmount={repair.invoice_amount} 
              invoicePaid={repair.invoice_paid}
              ticketNumber={ticketNumber}
              deposits={repair.deposits || []}
              totalDeposited={repair.total_deposited || 0}
              remainingBalance={repair.remaining_balance}
            />

            {/* Documents (Quotes & Invoices) */}
            <DocumentsSection ticketNumber={ticketNumber} />

            {/* Pickup Appointment (when status is ready) */}
            <PickupAppointmentSection ticketNumber={ticketNumber} repair={repair} />

            {/* Messages to Repair Shop */}
            <MessagesSection ticketNumber={ticketNumber} />

            {/* Status Message */}
            {repair.status === "ready" && !repair.pickup_appointment && (
              <Card className="bg-accent/10 border-accent/30">
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-12 h-12 bg-accent/20 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-accent" />
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground">Votre appareil est prêt !</h3>
                    <p className="text-sm text-muted-foreground">
                      Prenez rendez-vous ci-dessus pour venir le récupérer.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {repair.status === "delivered" && (
              <Card className="bg-green-500/10 border-green-500/30">
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-green-500" />
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground">Réparation terminée</h3>
                    <p className="text-sm text-muted-foreground">
                      Merci pour votre confiance !
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column - Timeline & Review */}
          <div className="space-y-6">
            {/* Timeline */}
            <Card className="bg-card border-border">
              <CardContent className="p-6">
                <h3 className="font-chivo text-lg font-medium text-foreground mb-6 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-primary" />
                  Suivi
                </h3>
                <StatusTimeline history={repair.status_history} currentStatus={repair.status} />
              </CardContent>
            </Card>

            {/* Review Section */}
            {reviewInfo && reviewInfo.can_review && (
              <ReviewForm ticketNumber={ticketNumber} onReviewSubmitted={fetchData} />
            )}
            
            {reviewInfo && reviewInfo.has_review && reviewInfo.review && (
              <ExistingReview review={reviewInfo.review} />
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border mt-12">
        <div className="max-w-4xl mx-auto px-4 py-6 text-center">
          <p className="text-sm text-muted-foreground">
            <span className="text-primary font-medium">{shopName}</span> - Votre spécialiste en réparation
          </p>
        </div>
      </footer>

      {/* Payment Cancelled Dialog */}
      <Dialog open={showCancelledDialog} onOpenChange={setShowCancelledDialog}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center">
                <XCircle className="w-8 h-8 text-amber-500" />
              </div>
            </div>
            <DialogTitle className="text-center text-xl">Paiement annulé</DialogTitle>
            <DialogDescription className="text-center text-muted-foreground mt-2">
              Votre paiement a été annulé. <strong>Aucun montant n'a été débité</strong> de votre carte bancaire.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 mt-6">
            <Button 
              onClick={handleRetryPayment}
              disabled={isRetrying}
              className="w-full bg-emerald-500 hover:bg-emerald-600"
            >
              {isRetrying ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Chargement...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Réessayer le paiement
                </>
              )}
            </Button>
            
            <Button 
              variant="outline" 
              onClick={handleCloseDialog}
              className="w-full"
            >
              Revenir au suivi de ma réparation
            </Button>
          </div>
          
          <p className="text-xs text-muted-foreground text-center mt-4">
            Si vous rencontrez des difficultés, contactez-nous.
          </p>
        </DialogContent>
      </Dialog>
      
      <Toaster position="top-right" richColors />
    </div>
  );
}
